/**
 * PresentialPaymentResult Page
 * 
 * Displays payment result after WebPay redirect for presential courses.
 * Shows success page with course details, or error/cancellation page.
 * 
 * Route: /pago-presencial/resultado
 * Requirements: 14.8, 14.10
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  ArrowLeft,
  Loader2,
  Calendar,
  MapPin,
  Mail,
  Phone
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { confirmPresentialPayment, parseWebPayParams, getPaymentResultType } from '../services/presentialPaymentApi';
import { formatCLP } from '../utils/priceFormat';

/**
 * PresentialPaymentResult Component
 * 
 * Handles the payment confirmation flow after WebPay redirect:
 * 1. Parses URL parameters from WebPay
 * 2. Calls payment confirmation API
 * 3. Displays appropriate result page
 * 
 * Requirements: 14.8, 14.10
 */
export default function PresentialPaymentResult() {
  const navigate = useNavigate();
  
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Process payment confirmation on mount
  useEffect(() => {
    async function processPayment() {
      try {
        // Parse WebPay parameters from URL
        const params = parseWebPayParams();
        const resultType = getPaymentResultType(params);

        console.log('Processing presential payment result:', { resultType, params });

        // If no valid parameters, show error
        if (resultType === 'unknown') {
          setError('No se encontraron parámetros de pago válidos.');
          setIsLoading(false);
          return;
        }

        // Confirm payment with backend
        const confirmResult = await confirmPresentialPayment(params);
        console.log('Presential payment confirmation result:', confirmResult);

        setResult(confirmResult);
      } catch (err) {
        console.error('Presential payment confirmation error:', err);
        setError(err.message || 'Error al confirmar el pago.');
      } finally {
        setIsLoading(false);
      }
    }

    processPayment();
  }, []);

  /**
   * Navigate to home page
   */
  const handleGoHome = () => {
    navigate('/');
  };

  /**
   * Navigate to courses page
   */
  const handleViewCourses = () => {
    navigate('/cursos');
  };

  return (
    <>
      <Header />
      <section className="min-h-screen py-24 bg-slate-900 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-blue-600/20 via-transparent to-transparent" />
          <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-cyan-500/20 via-transparent to-transparent" />
          <div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-8 md:p-10 shadow-2xl shadow-black/20">
              {/* Loading State */}
              {isLoading && (
                <LoadingState />
              )}

              {/* Error State (no result) */}
              {!isLoading && error && !result && (
                <ErrorPage message={error} onGoHome={handleGoHome} />
              )}

              {/* Result States */}
              {!isLoading && result && (
                <>
                  {result.status === 'approved' && (
                    <SuccessPage 
                      result={result} 
                      onGoHome={handleGoHome}
                      onViewCourses={handleViewCourses}
                    />
                  )}
                  {result.status === 'cancelled' && (
                    <CancelledPage 
                      result={result} 
                      onGoHome={handleGoHome} 
                    />
                  )}
                  {result.status === 'timeout' && (
                    <TimeoutPage 
                      result={result} 
                      onGoHome={handleGoHome} 
                    />
                  )}
                  {result.status === 'rejected' && (
                    <RejectedPage 
                      result={result} 
                      onGoHome={handleGoHome} 
                    />
                  )}
                  {result.status === 'error' && (
                    <ErrorPage 
                      message={result.errorMessage || 'Error desconocido'} 
                      onGoHome={handleGoHome} 
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

/**
 * Loading state component
 */
function LoadingState() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center py-12"
    >
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
        <Loader2 className="w-10 h-10 text-white animate-spin" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-3">
        Verificando Pago
      </h2>
      <p className="text-slate-400 max-w-md mx-auto">
        Estamos confirmando tu pago con WebPay. Por favor espera...
      </p>
    </motion.div>
  );
}

/**
 * Success page with payment summary and course details
 * Requirements: 14.8, 14.10
 */
function SuccessPage({ result, onGoHome, onViewCourses }) {
  const { transactionDetails, courseDetails, buyOrder } = result;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center"
    >
      {/* Success Icon */}
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
        <CheckCircle className="w-10 h-10 text-white" />
      </div>

      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
        ¡Pago Exitoso!
      </h2>
      <p className="text-slate-400 mb-8">
        Tu inscripción al curso presencial ha sido confirmada.
      </p>

      {/* Transaction Details */}
      {transactionDetails && (
        <div className="bg-white/5 rounded-2xl p-6 mb-6 text-left">
          <h3 className="text-lg font-semibold text-white mb-4">Detalles del Pago</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-400">Orden de compra:</span>
              <span className="text-white font-mono">{buyOrder}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Código de autorización:</span>
              <span className="text-white font-mono">{transactionDetails.authorizationCode}</span>
            </div>
            {transactionDetails.cardNumber && (
              <div className="flex justify-between">
                <span className="text-slate-400">Tarjeta:</span>
                <span className="text-white">**** {transactionDetails.cardNumber}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-slate-400">Monto:</span>
              <span className="text-white font-semibold">{formatCLP(transactionDetails.amount)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Course Details */}
      {courseDetails && (
        <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl p-6 mb-6 text-left border border-blue-400/20">
          <h3 className="text-lg font-semibold text-white mb-4">Información del Curso</h3>
          
          <div className="space-y-3">
            <p className="text-cyan-300 font-medium">{courseDetails.courseName}</p>
            
            {courseDetails.courseDate && (
              <div className="flex items-center gap-2 text-slate-300 text-sm">
                <Calendar className="w-4 h-4 text-cyan-400" />
                <span>Fecha: {courseDetails.courseDate}</span>
              </div>
            )}
            
            {courseDetails.courseLocation && (
              <div className="flex items-center gap-2 text-slate-300 text-sm">
                <MapPin className="w-4 h-4 text-cyan-400" />
                <span>Lugar: {courseDetails.courseLocation}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Next Steps */}
      <div className="bg-white/5 rounded-2xl p-6 mb-6 text-left">
        <h3 className="text-lg font-semibold text-white mb-4">Próximos Pasos</h3>
        <ul className="space-y-3 text-slate-300 text-sm">
          <li className="flex items-start gap-2">
            <Mail className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span>Recibirás un email de confirmación con todos los detalles del curso.</span>
          </li>
          <li className="flex items-start gap-2">
            <Phone className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span>Nos pondremos en contacto contigo antes del inicio del curso.</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span>Recuerda traer tu documento de identidad el día del curso.</span>
          </li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onViewCourses}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30 transition-all"
        >
          Ver Más Cursos
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onGoHome}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl border border-white/20 transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver al Inicio
        </motion.button>
      </div>

      <p className="text-sm text-slate-500 mt-6">
        También recibirás un email con estos datos de confirmación.
      </p>
    </motion.div>
  );
}


/**
 * Cancelled page when user aborts payment
 */
function CancelledPage({ result, onGoHome }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center"
    >
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
        <XCircle className="w-10 h-10 text-white" />
      </div>

      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
        Pago Cancelado
      </h2>
      <p className="text-slate-400 mb-8 max-w-md mx-auto">
        Has cancelado el proceso de pago. No se ha realizado ningún cargo a tu tarjeta.
      </p>

      {result?.buyOrder && (
        <p className="text-sm text-slate-500 mb-6">
          Referencia: {result.buyOrder}
        </p>
      )}

      <div className="bg-white/5 rounded-xl p-4 mb-8">
        <p className="text-slate-300 text-sm">
          Si deseas completar tu inscripción, puedes usar el enlace de pago que recibiste por correo electrónico.
        </p>
      </div>

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onGoHome}
        className="flex items-center justify-center gap-2 px-6 py-3 mx-auto bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl border border-white/20 transition-all"
      >
        <ArrowLeft className="w-5 h-5" />
        Volver al Inicio
      </motion.button>
    </motion.div>
  );
}

/**
 * Timeout page when payment times out
 */
function TimeoutPage({ result, onGoHome }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center"
    >
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
        <Clock className="w-10 h-10 text-white" />
      </div>

      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
        Tiempo Agotado
      </h2>
      <p className="text-slate-400 mb-8 max-w-md mx-auto">
        El tiempo para completar el pago ha expirado. No se ha realizado ningún cargo a tu tarjeta.
      </p>

      {result?.buyOrder && (
        <p className="text-sm text-slate-500 mb-6">
          Referencia: {result.buyOrder}
        </p>
      )}

      <div className="bg-white/5 rounded-xl p-4 mb-8">
        <p className="text-slate-300 text-sm">
          Puedes intentar nuevamente usando el enlace de pago que recibiste por correo electrónico.
        </p>
      </div>

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onGoHome}
        className="flex items-center justify-center gap-2 px-6 py-3 mx-auto bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl border border-white/20 transition-all"
      >
        <ArrowLeft className="w-5 h-5" />
        Volver al Inicio
      </motion.button>
    </motion.div>
  );
}

/**
 * Rejected page when payment is declined
 */
function RejectedPage({ result, onGoHome }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center"
    >
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center shadow-lg shadow-red-500/30">
        <XCircle className="w-10 h-10 text-white" />
      </div>

      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
        Pago Rechazado
      </h2>
      <p className="text-slate-400 mb-4 max-w-md mx-auto">
        {result?.rejectionReason || result?.errorMessage || 'Tu pago no pudo ser procesado.'}
      </p>
      <p className="text-sm text-slate-500 mb-8">
        Por favor verifica los datos de tu tarjeta o intenta con otro medio de pago.
      </p>

      {result?.buyOrder && (
        <p className="text-sm text-slate-500 mb-6">
          Referencia: {result.buyOrder}
        </p>
      )}

      <div className="bg-white/5 rounded-xl p-4 mb-8">
        <p className="text-slate-300 text-sm">
          Puedes intentar nuevamente usando el enlace de pago que recibiste por correo electrónico.
        </p>
      </div>

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onGoHome}
        className="flex items-center justify-center gap-2 px-6 py-3 mx-auto bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl border border-white/20 transition-all"
      >
        <ArrowLeft className="w-5 h-5" />
        Volver al Inicio
      </motion.button>
    </motion.div>
  );
}

/**
 * Generic error page
 */
function ErrorPage({ message, onGoHome }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center"
    >
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center shadow-lg shadow-red-500/30">
        <AlertCircle className="w-10 h-10 text-white" />
      </div>

      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
        Error
      </h2>
      <p className="text-slate-400 mb-8 max-w-md mx-auto">
        {message}
      </p>

      <div className="bg-white/5 rounded-xl p-4 mb-8">
        <p className="text-slate-300 text-sm">
          Si el problema persiste, por favor contacta al Instituto Valparaíso.
        </p>
      </div>

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onGoHome}
        className="flex items-center justify-center gap-2 px-6 py-3 mx-auto bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl border border-white/20 transition-all"
      >
        <ArrowLeft className="w-5 h-5" />
        Volver al Inicio
      </motion.button>
    </motion.div>
  );
}
